"use client";
import Link from "next/link";
import { Mascot } from "@/components/landing/Mascot";

// Real game sprites — a friendly row of Surggies + the CEO leading them.
const SPRITES = [
  { key: "ceo_1", label: "CEO" },
  { key: "surggie_2", label: "Analyst" },
  { key: "surggie_5", label: "Designer" },
  { key: "surggie_8", label: "Dev" },
  { key: "surggie_11", label: "Marketer" },
];

function Sparkle({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute block ${className}`}
      style={{
        width: 18,
        height: 18,
        background: "var(--gold-bright)",
        clipPath:
          "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
        animation: "sparkle-twinkle 2.4s ease-in-out infinite",
      }}
    />
  );
}

export function LandingHero() {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-16 overflow-hidden bg-cream">
      {/* Sunny sun-glow + soft accent washes */}
      <div className="absolute inset-0 pointer-events-none section-sun">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[820px] h-[620px] bg-[var(--gold)]/15 rounded-full blur-[130px]" />
        <div className="absolute top-1/3 left-1/4 w-[420px] h-[420px] bg-[var(--gold-bright)]/15 rounded-full blur-[110px]" />
      </div>

      {/* Floating sparkles */}
      <Sparkle className="top-[18%] left-[16%]" />
      <Sparkle className="top-[26%] right-[18%]" />
      <Sparkle className="bottom-[26%] left-[24%]" />

      {/* Real bouncing Surggie mascots */}
      <div className="relative z-10 flex items-end gap-3 sm:gap-6 mb-10">
        {SPRITES.map((s, i) => (
          <div
            key={s.label}
            className="flex flex-col items-center gap-2 float-bob"
            style={{ animationDelay: `${i * 0.22}s` }}
          >
            <Mascot spriteKey={s.key} scale={4} bob={false} />
            <span
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white border-2 border-[var(--ink)] text-[var(--ink)] sticker-shadow-sm"
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>

      <div className="relative z-10 pop-in">
        <h1
          className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black leading-[0.9] tracking-tight text-[var(--ink)] mb-6"
          style={{ fontFamily: "var(--font-manrope)" }}
        >
          Get Your{" "}
          <span className="gold-shimmer">Surggies</span>
        </h1>
        <p className="text-lg md:text-xl text-[var(--ink-soft)] font-mono max-w-2xl mx-auto mb-10 leading-relaxed">
          Hire AI agents to run your business departments — all inside a pixel RPG you actually want to use.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            href="/register"
            className="btn-gold sticker-press text-base px-8 py-4 font-mono uppercase tracking-wider"
            style={{ fontFamily: "var(--font-manrope)" }}
          >
            Start Building Free
          </Link>
          <a
            href="#solution"
            className="text-[var(--ink-soft)] hover:text-[var(--ink)] font-mono text-sm underline underline-offset-4 decoration-[var(--gold-deep)] transition-colors"
          >
            See how it works
          </a>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[var(--ink)] rounded-full flex items-start justify-center pt-1 bg-white">
          <div className="w-1.5 h-3 bg-[var(--gold)] rounded-full" />
        </div>
      </div>
    </section>
  );
}
